/**
 * BubbleChatWidget — Core widget class for the floating AI chat bubble.
 * Uses plain DOM APIs; runs inside a Shadow DOM for CSS isolation.
 */
import { apiFetch } from "./api.js";
import { renderMarkdown } from "./markdown.js";

const STORAGE_KEY_SESSION = "frappeai_bubble_session";
const STORAGE_KEY_POSITION = "frappeai_bubble_position";

// ─── SVG Icons ───
const ICON_CHAT = `<svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
const ICON_CLOSE = `<svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const ICON_EXTEND = `<svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
const ICON_NEW_CHAT = `<svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
const ICON_SEND = `<svg viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;
const ICON_EMPTY = `<svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/></svg>`;

export class BubbleChatWidget {
	constructor(shadowRoot, settings) {
		this.shadow = shadowRoot;
		this.sessionName = null;
		this.sessionTitle = null;
		this.messages = [];
		this.sending = false;
		this.isOpen = false;
		this.loaded = false;
		this.settings = settings || { enabled: true, logo: null, title: "AI Chat" };
		// Guest mode (public website): no AI Chat Session, stateless Q&A via
		// ask_wiki_public, conversation history kept in memory only.
		this.guestMode = !!(settings && settings.guestMode);

		// DOM references
		this.bubbleBtn = null;
		this.chatPanel = null;
		this.messagesContainer = null;
		this.inputEl = null;
		this.sendBtn = null;
		this.headerTitle = null;
		this.loadingEl = null;

		this.init();
	}

	init() {
		this.bubbleBtn = this.createBubbleButton();
		this.chatPanel = this.createChatPanel();
		this.shadow.appendChild(this.bubbleBtn);
		this.shadow.appendChild(this.chatPanel);
		this.restorePosition();
		this.initDrag();
		this.initImageLightbox();
	}

	// ─── Image lightbox ───
	// Markdown images rendered inside .msg-content are scaled by CSS to fit
	// the chat panel; this handler opens them full-size when clicked, with the
	// overlay lying inside the shadow root for style isolation.
	initImageLightbox() {
		this.messagesContainer.addEventListener("click", (e) => {
			const img = e.target.closest && e.target.closest("img");
			if (!img || !img.closest(".msg-content")) return;
			e.preventDefault();
			this.showLightbox(img.currentSrc || img.src, img.alt || "");
		});
	}

	showLightbox(src, alt) {
		if (!src) return;
		const overlay = document.createElement("div");
		overlay.className = "lightbox";
		const big = document.createElement("img");
		big.src = src;
		big.alt = alt || "";
		overlay.appendChild(big);
		const close = () => {
			overlay.remove();
			document.removeEventListener("keydown", onEsc);
		};
		const onEsc = (e) => {
			if (e.key === "Escape") close();
		};
		overlay.addEventListener("click", close);
		document.addEventListener("keydown", onEsc);
		this.shadow.appendChild(overlay);
	}

	// ─── Bubble Button ───
	createBubbleButton() {
		const btn = document.createElement("button");
		btn.className = "bubble-btn";

		if (this.settings.logo) {
			// Custom logo from AI Settings
			const img = document.createElement("img");
			img.src = this.settings.logo;
			img.alt = "AI Chat";
			img.className = "bubble-logo";
			img.draggable = false;
			btn.appendChild(img);
			btn.classList.add("has-logo");
		} else {
			// Default SVG icon
			btn.innerHTML = ICON_CHAT;
		}

		btn.setAttribute("aria-label", "Open AI Chat");
		btn.addEventListener("click", (e) => {
			// Ignore click if was a drag
			if (this._wasDragged) {
				this._wasDragged = false;
				return;
			}
			this.toggle();
		});
		return btn;
	}

	// ─── Chat Panel ───
	createChatPanel() {
		const panel = document.createElement("div");
		panel.className = "chat-panel";

		// Header
		const header = document.createElement("div");
		header.className = "chat-header";

		this.headerTitle = document.createElement("div");
		this.headerTitle.className = "chat-header-title";
		this.headerTitle.textContent = this.settings.title || "AI Chat";

		const closeBtn = this.createHeaderBtn(ICON_CLOSE, "Close", () => this.toggle());

		header.appendChild(this.headerTitle);
		// New-chat + open-full-chat rely on AI Chat Sessions / the SPA, which
		// guests can't access — only add them for authenticated users.
		if (!this.guestMode) {
			const newChatBtn = this.createHeaderBtn(ICON_NEW_CHAT, "New Chat", () => this.onNewChat());
			const extendBtn = this.createHeaderBtn(ICON_EXTEND, "Open full chat", () => this.onExtend());
			header.appendChild(newChatBtn);
			header.appendChild(extendBtn);
		}
		header.appendChild(closeBtn);

		// Messages area
		this.messagesContainer = document.createElement("div");
		this.messagesContainer.className = "chat-messages";

		// Empty state
		this.emptyState = document.createElement("div");
		this.emptyState.className = "chat-empty";
		this.emptyState.innerHTML = `${ICON_EMPTY}<div class="chat-empty-text">Start a conversation with AI</div>`;
		this.messagesContainer.appendChild(this.emptyState);

		// Loading dots
		this.loadingEl = document.createElement("div");
		this.loadingEl.className = "loading-dots";
		this.loadingEl.style.display = "none";
		this.loadingEl.innerHTML = `<div class="dot"></div><div class="dot"></div><div class="dot"></div>`;

		// Input area
		const inputArea = document.createElement("div");
		inputArea.className = "chat-input-area";

		this.inputEl = document.createElement("textarea");
		this.inputEl.className = "chat-input";
		this.inputEl.placeholder = "Ask AI anything...";
		this.inputEl.rows = 1;
		// Stop all keyboard events from bubbling to Frappe's shortcut handler
		this.inputEl.addEventListener("keydown", (e) => {
			e.stopPropagation();
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				this.handleSend();
			}
		});
		this.inputEl.addEventListener("keyup", (e) => e.stopPropagation());
		this.inputEl.addEventListener("keypress", (e) => e.stopPropagation());
		this.inputEl.addEventListener("input", () => this.autoResizeInput());

		this.sendBtn = document.createElement("button");
		this.sendBtn.className = "chat-send-btn";
		this.sendBtn.innerHTML = ICON_SEND;
		this.sendBtn.setAttribute("aria-label", "Send message");
		this.sendBtn.addEventListener("click", () => this.handleSend());

		inputArea.appendChild(this.inputEl);
		inputArea.appendChild(this.sendBtn);

		panel.appendChild(header);
		panel.appendChild(this.messagesContainer);
		panel.appendChild(inputArea);

		return panel;
	}

	createHeaderBtn(iconSvg, title, onClick) {
		const btn = document.createElement("button");
		btn.className = "chat-header-btn";
		btn.innerHTML = iconSvg;
		btn.title = title;
		btn.addEventListener("click", onClick);
		return btn;
	}

	// ─── Toggle Panel ───
	async toggle() {
		this.isOpen = !this.isOpen;

		if (this.isOpen) {
			this.chatPanel.classList.add("is-visible");
			this.bubbleBtn.classList.add("is-open");
			this.positionPanel();
			if (!this.loaded) {
				if (this.guestMode) {
					// No AI Chat Session for guests — render the empty state only.
					this.messages = [];
					this.renderMessages();
					this.loaded = true;
				} else {
					await this.ensureSession();
				}
			}
			this.inputEl.focus();
		} else {
			this.chatPanel.classList.remove("is-visible");
			this.bubbleBtn.classList.remove("is-open");
		}
	}

	positionPanel() {
		const rect = this.bubbleBtn.getBoundingClientRect();
		const panelWidth = Math.min(400, window.innerWidth - 32);
		const panelHeight = Math.min(520, window.innerHeight - 120);

		// Position above the bubble
		let bottom = window.innerHeight - rect.top + 12;
		let right = window.innerWidth - rect.right;

		// Flip to below if not enough space above
		if (rect.top < panelHeight + 20) {
			bottom = window.innerHeight - rect.bottom - panelHeight - 12;
		}

		// Keep panel on screen
		if (right + panelWidth > window.innerWidth - 8) {
			right = 8;
		}
		if (right < 8) right = 8;

		this.chatPanel.style.bottom = `${Math.max(8, bottom)}px`;
		this.chatPanel.style.right = `${right}px`;
		this.chatPanel.style.width = `${panelWidth}px`;
		this.chatPanel.style.height = `${panelHeight}px`;
	}

	// ─── Session Management ───
	async ensureSession() {
		try {
			// Try to resume from localStorage
			const storedSession = localStorage.getItem(STORAGE_KEY_SESSION);
			if (storedSession) {
				try {
					const data = await apiFetch("frappeai.api.get_messages", {
						session_name: storedSession,
					});
					this.sessionName = storedSession;
					this.sessionTitle = data.session.title || "AI Chat";
					this.messages = data.messages || [];
					this.renderMessages();
					this.loaded = true;
					this.updateTitle();
					return;
				} catch (e) {
					// Session no longer valid, fall through
					localStorage.removeItem(STORAGE_KEY_SESSION);
				}
			}

			// Try to get the most recent session
			const sessions = await apiFetch("frappeai.api.get_sessions", {
				limit: 1,
			});
			if (sessions && sessions.length > 0) {
				const session = sessions[0];
				this.sessionName = session.name;
				this.sessionTitle = session.title || "AI Chat";
				localStorage.setItem(STORAGE_KEY_SESSION, this.sessionName);

				const data = await apiFetch("frappeai.api.get_messages", {
					session_name: this.sessionName,
				});
				this.messages = data.messages || [];
				this.renderMessages();
				this.loaded = true;
				this.updateTitle();
				return;
			}

			// No sessions exist — create a new one
			await this.createNewSession();
		} catch (err) {
			this.showError("Failed to load chat session. Please try again.");
			console.error("[BubbleChat] ensureSession error:", err);
		}
	}

	async createNewSession() {
		const result = await apiFetch("frappeai.api.create_session", {
			title: "New Chat",
		});
		this.sessionName = result.session_name;
		this.sessionTitle = result.title || "New Chat";
		this.messages = [];
		localStorage.setItem(STORAGE_KEY_SESSION, this.sessionName);
		this.renderMessages();
		this.loaded = true;
		this.updateTitle();
	}

	updateTitle() {
		if (this.headerTitle) {
			this.headerTitle.textContent = this.sessionTitle || "AI Chat";
		}
	}

	// ─── Message Rendering ───
	renderMessages() {
		// Clear existing messages
		this.messagesContainer.innerHTML = "";

		if (this.messages.length === 0) {
			this.messagesContainer.appendChild(this.emptyState);
			return;
		}

		for (const msg of this.messages) {
			if (msg.role === "system") continue;
			const el = this.createMessageEl(msg);
			this.messagesContainer.appendChild(el);
		}

		// Re-append loading dots if sending
		if (this.sending) {
			this.loadingEl.style.display = "flex";
			this.messagesContainer.appendChild(this.loadingEl);
		}

		this.scrollToBottom();
	}

	createMessageEl(msg) {
		const wrapper = document.createElement("div");
		const isUser = msg.role === "user";

		wrapper.className = `msg ${isUser ? "msg-user" : "msg-assistant"}`;

		const content = document.createElement("div");
		content.className = "msg-content";

		if (isUser) {
			content.textContent = msg.content || "";
		} else {
			content.innerHTML = renderMarkdown(msg.content || "");
		}

		wrapper.appendChild(content);

		// Wiki sources for assistant messages
		const rawSources = !isUser ? (msg.wiki_sources || msg.sources) : null;
		if (rawSources) {
			try {
				const sources = typeof rawSources === "string"
					? JSON.parse(rawSources)
					: rawSources;

				if (Array.isArray(sources) && sources.length > 0) {
					const sourcesDiv = document.createElement("div");
					sourcesDiv.className = "msg-sources";
					for (const src of sources) {
						const badge = document.createElement("a");
						badge.className = "msg-source-badge";
						// API returns wiki_title/wiki_route; stored messages may use title/route
						const title = src.wiki_title || src.title || src.wiki_page || src.page || "Source";
						badge.textContent = title;
						const route = src.wiki_route || src.route || src.url || "";
						if (route) {
							badge.href = route.startsWith("/") ? route : `/${route}`;
							badge.target = "_blank";
							badge.rel = "noopener";
						}
						sourcesDiv.appendChild(badge);
					}
					wrapper.appendChild(sourcesDiv);
				}
			} catch (e) {
				// ignore malformed sources
			}
		}

		return wrapper;
	}

	appendMessage(msg) {
		// Remove empty state if present
		if (this.emptyState.parentNode === this.messagesContainer) {
			this.messagesContainer.removeChild(this.emptyState);
		}

		this.messages.push(msg);
		const el = this.createMessageEl(msg);

		// Insert before loading dots if visible
		if (this.loadingEl.parentNode === this.messagesContainer) {
			this.messagesContainer.insertBefore(el, this.loadingEl);
		} else {
			this.messagesContainer.appendChild(el);
		}

		this.scrollToBottom();
	}

	showLoading() {
		this.loadingEl.style.display = "flex";
		if (this.loadingEl.parentNode !== this.messagesContainer) {
			this.messagesContainer.appendChild(this.loadingEl);
		}
		this.scrollToBottom();
	}

	hideLoading() {
		this.loadingEl.style.display = "none";
		if (this.loadingEl.parentNode) {
			this.loadingEl.parentNode.removeChild(this.loadingEl);
		}
	}

	showError(text) {
		const errEl = document.createElement("div");
		errEl.className = "msg-error";
		errEl.textContent = text;
		this.messagesContainer.appendChild(errEl);
		this.scrollToBottom();
	}

	scrollToBottom() {
		requestAnimationFrame(() => {
			this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
		});
	}

	autoResizeInput() {
		this.inputEl.style.height = "auto";
		this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + "px";
	}

	// ─── Send Message ───
	async handleSend() {
		const text = (this.inputEl.value || "").trim();
		if (!text || this.sending) return;

		this.inputEl.value = "";
		this.inputEl.style.height = "auto";
		this.sending = true;
		this.sendBtn.disabled = true;

		// Optimistic: show user message immediately
		this.appendMessage({ role: "user", content: text });
		this.showLoading();

		try {
			let result;
			if (this.guestMode) {
				// Stateless public Q&A — send in-memory history (exclude the
				// just-added user turn, the backend appends the question).
				const history = this.messages
					.filter((m) => m.role === "user" || m.role === "assistant")
					.slice(0, -1)
					.slice(-10)
					.map((m) => ({ role: m.role, content: m.content }));
				result = await apiFetch("frappeai.api.ask_wiki_public", {
					question: text,
					history: JSON.stringify(history),
				});
			} else {
				result = await apiFetch("frappeai.api.chat", {
					session_name: this.sessionName,
					message: text,
				});
				// Update title if auto-generated
				if (result.session_title) {
					this.sessionTitle = result.session_title;
					this.updateTitle();
				}
			}

			this.hideLoading();

			// Append assistant response
			this.appendMessage({
				role: "assistant",
				content: result.answer || result.response || "",
				wiki_sources: result.sources || null,
			});
		} catch (err) {
			this.hideLoading();
			this.showError("Failed to get AI response. Please try again.");
			console.error("[BubbleChat] sendMessage error:", err);
		} finally {
			this.sending = false;
			this.sendBtn.disabled = false;
			this.inputEl.focus();
		}
	}

	// ─── Actions ───
	onExtend() {
		if (this.sessionName) {
			window.location.href = `/ai-chat/${this.sessionName}`;
		} else {
			window.location.href = "/ai-chat";
		}
	}

	async onNewChat() {
		try {
			this.loaded = false;
			await this.createNewSession();
			this.inputEl.focus();
		} catch (err) {
			this.showError("Failed to create new chat.");
			console.error("[BubbleChat] onNewChat error:", err);
		}
	}

	// ─── Draggable Bubble ───
	restorePosition() {
		try {
			const saved = localStorage.getItem(STORAGE_KEY_POSITION);
			if (saved) {
				const pos = JSON.parse(saved);
				this.bubbleBtn.style.right = pos.right + "px";
				this.bubbleBtn.style.bottom = pos.bottom + "px";
				this.bubbleBtn.style.left = "auto";
				this.bubbleBtn.style.top = "auto";
			}
		} catch (e) {
			// use default position
		}
	}

	savePosition() {
		const rect = this.bubbleBtn.getBoundingClientRect();
		const pos = {
			right: window.innerWidth - rect.right,
			bottom: window.innerHeight - rect.bottom,
		};
		try {
			localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(pos));
		} catch (e) {
			// ignore
		}
	}

	initDrag() {
		let isDragging = false;
		let startX = 0;
		let startY = 0;
		let startRight = 0;
		let startBottom = 0;
		let moved = false;

		const onPointerDown = (e) => {
			if (e.button && e.button !== 0) return; // left click only
			isDragging = true;
			moved = false;
			this._wasDragged = false;

			const rect = this.bubbleBtn.getBoundingClientRect();
			startX = e.clientX;
			startY = e.clientY;
			startRight = window.innerWidth - rect.right;
			startBottom = window.innerHeight - rect.bottom;

			this.bubbleBtn.setPointerCapture(e.pointerId);
			e.preventDefault();
		};

		const onPointerMove = (e) => {
			if (!isDragging) return;

			const dx = e.clientX - startX;
			const dy = e.clientY - startY;

			if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
				moved = true;
			}

			const newRight = Math.max(4, Math.min(window.innerWidth - 60, startRight - dx));
			const newBottom = Math.max(4, Math.min(window.innerHeight - 60, startBottom - dy));

			this.bubbleBtn.style.right = newRight + "px";
			this.bubbleBtn.style.bottom = newBottom + "px";
			this.bubbleBtn.style.left = "auto";
			this.bubbleBtn.style.top = "auto";

			// Reposition panel if open
			if (this.isOpen) {
				this.positionPanel();
			}
		};

		const onPointerUp = (e) => {
			if (!isDragging) return;
			isDragging = false;

			if (moved) {
				this._wasDragged = true;
				this.savePosition();
			}

			this.bubbleBtn.releasePointerCapture(e.pointerId);
		};

		this.bubbleBtn.addEventListener("pointerdown", onPointerDown);
		this.bubbleBtn.addEventListener("pointermove", onPointerMove);
		this.bubbleBtn.addEventListener("pointerup", onPointerUp);
	}
}
